/**
 * ConsolidatedPartsAnalysis Component
 *
 * Sortable table showing per-location parts metrics in consolidated view.
 * Displays gross by pay type (6 categories), controllable expenses %, and projected gross.
 */

import React, { useMemo, useCallback } from 'react';
import {
  CogIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  BarsArrowDownIcon,
} from '@heroicons/react/24/outline';
import {
  ConsolidatedPartsLocationData,
  ConsolidatedPartsSortField,
  ConsolidatedPartsSortConfig,
} from '../../types/consolidatedParts';

interface ConsolidatedPartsAnalysisProps {
  data: ConsolidatedPartsLocationData[];
  year: number;
  month: number;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  sortConfig: ConsolidatedPartsSortConfig;
  onSortChange: (config: ConsolidatedPartsSortConfig) => void;
}

/** Column definition for sortable headers */
const SORT_COLUMNS: Array<{
  field: ConsolidatedPartsSortField;
  label: string;
  shortLabel: string;
  align: 'left' | 'right';
  format: 'currency' | 'number' | 'percent';
}> = [
  { field: 'locationName', label: 'Location', shortLabel: 'Location', align: 'left', format: 'number' },
  { field: 'totalPartsGross', label: 'Total Gross', shortLabel: 'Total', align: 'right', format: 'currency' },
  { field: 'projectedGross', label: 'Projected', shortLabel: 'Proj.', align: 'right', format: 'currency' },
  { field: 'customerParts', label: 'Customer', shortLabel: 'Cust.', align: 'right', format: 'currency' },
  { field: 'warrantyParts', label: 'Warranty', shortLabel: 'Warr.', align: 'right', format: 'currency' },
  { field: 'internalParts', label: 'Internal', shortLabel: 'Int.', align: 'right', format: 'currency' },
  { field: 'counterRetail', label: 'Ctr. Retail', shortLabel: 'CtrR', align: 'right', format: 'currency' },
  { field: 'counterWholesale', label: 'Ctr. Wholesale', shortLabel: 'CtrW', align: 'right', format: 'currency' },
  { field: 'otherParts', label: 'Other / Inv. Adj.', shortLabel: 'Other', align: 'right', format: 'currency' },
  { field: 'controllableExpensePct', label: 'Ctrl. Exp. %', shortLabel: 'Ctrl%', align: 'right', format: 'percent' },
];

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

function getMonthName(month: number): string {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return months[month - 1] || '';
}

/** Get the sort value for a row based on the field */
function getSortValue(row: ConsolidatedPartsLocationData, field: ConsolidatedPartsSortField): string | number {
  switch (field) {
    case 'locationName': return row.locationName;
    case 'totalPartsGross': return row.totalPartsGross;
    case 'projectedGross': return row.projectedGross ?? 0;
    case 'customerParts': return row.partsGrossByPayType.customerParts;
    case 'warrantyParts': return row.partsGrossByPayType.warrantyParts;
    case 'internalParts': return row.partsGrossByPayType.internalParts;
    case 'counterRetail': return row.partsGrossByPayType.counterRetail;
    case 'counterWholesale': return row.partsGrossByPayType.counterWholesale;
    case 'otherParts': return row.partsGrossByPayType.otherParts;
    case 'controllableExpensePct': return row.controllableExpensePct;
    default: return 0;
  }
}

/** Get the display value for a cell */
function getCellValue(row: ConsolidatedPartsLocationData, field: ConsolidatedPartsSortField): string {
  switch (field) {
    case 'locationName': return row.locationName;
    case 'totalPartsGross': return formatCurrency(row.totalPartsGross);
    case 'projectedGross': return row.projectedGross != null ? formatCurrency(row.projectedGross) : '—';
    case 'customerParts': return formatCurrency(row.partsGrossByPayType.customerParts);
    case 'warrantyParts': return formatCurrency(row.partsGrossByPayType.warrantyParts);
    case 'internalParts': return formatCurrency(row.partsGrossByPayType.internalParts);
    case 'counterRetail': return formatCurrency(row.partsGrossByPayType.counterRetail);
    case 'counterWholesale': return formatCurrency(row.partsGrossByPayType.counterWholesale);
    case 'otherParts': return formatCurrency(row.partsGrossByPayType.otherParts);
    case 'controllableExpensePct': return formatPercent(row.controllableExpensePct);
    default: return '';
  }
}

/** Get color class for Total Gross based on whether it meets projected goal */
function getGrossColorClass(row: ConsolidatedPartsLocationData): string {
  if (row.projectedGross == null || row.projectedGross <= 0) return 'text-gray-700';
  return row.totalPartsGross >= row.projectedGross ? 'text-green-600' : 'text-red-600';
}

export const ConsolidatedPartsAnalysis: React.FC<ConsolidatedPartsAnalysisProps> = ({
  data,
  year,
  month,
  collapsed,
  onToggleCollapse,
  sortConfig,
  onSortChange,
}) => {
  // Sort data
  const sortedData = useMemo(() => {
    const sorted = [...data].sort((a, b) => {
      const aVal = getSortValue(a, sortConfig.field);
      const bVal = getSortValue(b, sortConfig.field);

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortConfig.direction === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      const numA = aVal as number;
      const numB = bVal as number;
      return sortConfig.direction === 'asc' ? numA - numB : numB - numA;
    });
    return sorted;
  }, [data, sortConfig]);

  // Calculate totals for footer row
  const totals = useMemo(() => {
    const totalGross = data.reduce((sum, d) => sum + d.totalPartsGross, 0);
    const totalCustomer = data.reduce((sum, d) => sum + d.partsGrossByPayType.customerParts, 0);
    const totalWarranty = data.reduce((sum, d) => sum + d.partsGrossByPayType.warrantyParts, 0);
    const totalInternal = data.reduce((sum, d) => sum + d.partsGrossByPayType.internalParts, 0);
    const totalCtrRetail = data.reduce((sum, d) => sum + d.partsGrossByPayType.counterRetail, 0);
    const totalCtrWholesale = data.reduce((sum, d) => sum + d.partsGrossByPayType.counterWholesale, 0);
    const totalOther = data.reduce((sum, d) => sum + d.partsGrossByPayType.otherParts, 0);
    const totalCtrlExp = data.reduce((sum, d) => sum + d.totalControllableExpenses, 0);
    const totalProjected = data.reduce((sum, d) => sum + (d.projectedGross ?? 0), 0);
    const hasAnyProjection = data.some(d => d.projectedGross != null);
    const ctrlExpPct = totalGross !== 0 ? (totalCtrlExp / totalGross) * 100 : 0;

    return {
      totalGross,
      projectedGross: hasAnyProjection ? totalProjected : null,
      customerParts: totalCustomer,
      warrantyParts: totalWarranty,
      internalParts: totalInternal,
      counterRetail: totalCtrRetail,
      counterWholesale: totalCtrWholesale,
      otherParts: totalOther,
      ctrlExpPct,
    };
  }, [data]);

  // Handle sort column click
  const handleSort = useCallback(
    (field: ConsolidatedPartsSortField) => {
      onSortChange({
        field,
        direction:
          sortConfig.field === field && sortConfig.direction === 'desc'
            ? 'asc'
            : 'desc',
      });
    },
    [sortConfig, onSortChange]
  );

  // Render sort indicator
  const renderSortIndicator = (field: ConsolidatedPartsSortField) => {
    if (sortConfig.field !== field) {
      return <BarsArrowDownIcon className="h-3.5 w-3.5 text-gray-300" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ChevronUpIcon className="h-3.5 w-3.5 text-green-600" />
    ) : (
      <ChevronDownIcon className="h-3.5 w-3.5 text-green-600" />
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div
        className={`px-4 py-3 ${collapsed ? '' : 'border-b border-gray-200'} ${onToggleCollapse ? 'cursor-pointer select-none hover:bg-gray-50 transition-colors' : ''}`}
        onClick={onToggleCollapse}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-100 rounded-lg">
            <CogIcon className="h-5 w-5 text-green-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">
              Parts Department Analysis by Location
            </h3>
            <p className="text-sm text-gray-500">
              {getMonthName(month)} {year} &middot; {data.length} locations
            </p>
          </div>
          {onToggleCollapse && (
            <ChevronDownIcon
              className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${collapsed ? '' : 'rotate-180'}`}
            />
          )}
        </div>
      </div>

      {/* Content - Sortable Table */}
      {!collapsed && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {SORT_COLUMNS.map((col) => (
                  <th
                    key={col.field}
                    className={`px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-100 transition-colors ${
                      col.align === 'right' ? 'text-right' : 'text-left'
                    }`}
                    onClick={() => handleSort(col.field)}
                  >
                    <div className={`flex items-center gap-1 ${col.align === 'right' ? 'justify-end' : ''}`}>
                      <span className="hidden md:inline">{col.label}</span>
                      <span className="md:hidden">{col.shortLabel}</span>
                      {renderSortIndicator(col.field)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {sortedData.map((row) => (
                <tr
                  key={row.locationId}
                  className="hover:bg-green-50 transition-colors"
                >
                  {SORT_COLUMNS.map((col) => {
                    let colorClass = col.field === 'locationName' ? 'font-medium text-gray-900' : 'text-gray-700';
                    if (col.field === 'totalPartsGross') {
                      colorClass = `font-semibold ${getGrossColorClass(row)}`;
                    }
                    return (
                      <td
                        key={col.field}
                        className={`px-3 py-2.5 text-sm whitespace-nowrap ${
                          col.align === 'right' ? 'text-right' : 'text-left'
                        } ${colorClass}`}
                      >
                        {getCellValue(row, col.field)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
            {/* Footer totals row */}
            <tfoot>
              <tr className="bg-gray-50 border-t-2 border-gray-300">
                <td className="px-3 py-2.5 text-sm font-bold text-gray-900">
                  Total
                </td>
                <td className={`px-3 py-2.5 text-sm font-bold text-right ${
                  totals.projectedGross != null && totals.projectedGross > 0
                    ? (totals.totalGross >= totals.projectedGross ? 'text-green-600' : 'text-red-600')
                    : 'text-gray-900'
                }`}>
                  {formatCurrency(totals.totalGross)}
                </td>
                <td className="px-3 py-2.5 text-sm font-bold text-gray-900 text-right">
                  {totals.projectedGross != null ? formatCurrency(totals.projectedGross) : '—'}
                </td>
                <td className="px-3 py-2.5 text-sm font-bold text-gray-900 text-right">
                  {formatCurrency(totals.customerParts)}
                </td>
                <td className="px-3 py-2.5 text-sm font-bold text-gray-900 text-right">
                  {formatCurrency(totals.warrantyParts)}
                </td>
                <td className="px-3 py-2.5 text-sm font-bold text-gray-900 text-right">
                  {formatCurrency(totals.internalParts)}
                </td>
                <td className="px-3 py-2.5 text-sm font-bold text-gray-900 text-right">
                  {formatCurrency(totals.counterRetail)}
                </td>
                <td className="px-3 py-2.5 text-sm font-bold text-gray-900 text-right">
                  {formatCurrency(totals.counterWholesale)}
                </td>
                <td className="px-3 py-2.5 text-sm font-bold text-gray-900 text-right">
                  {formatCurrency(totals.otherParts)}
                </td>
                <td className="px-3 py-2.5 text-sm font-bold text-gray-900 text-right">
                  {formatPercent(totals.ctrlExpPct)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};
